import { eventDatesBetween } from "./eventDays";
// Resolve the doors-open / doors-close window for a given festival
// day. Multi-day briefings can have different door times per day
// (a 4-day festival might run 12-23 on day 1-3 and 14-03 on day 4).
//
// Schema:
//   briefing_day_doors  — canonical per-day overrides
//   briefings.doors_open / doors_close — single-row fallback for
//   legacy briefings + the producer's default when they haven't
//   tweaked a specific day.
//
// Lives in the shared package so web and mobile resolve doors
// identically. Web's apps/web/src/lib/doorsForDay.ts re-exports
// these alongside its diffDayDoors save helper (which is form-
// specific and stays in the web app).
//
// Convention: these helpers do NOT fabricate placeholder times when
// the producer hasn't configured doors. They preserve absent values
// (null / empty string / null return) so each caller decides what
// "no data" means in its own context — UI inputs may want a 12:00
// pre-fill, but Analytics shouldn't run an uptime calc against a
// made-up window, and the AI shouldn't hallucinate doors that were
// never set.

export type DayDoors = {
  event_date: string; // ISO yyyy-mm-dd
  doors_open: string | null; // HH:MM or HH:MM:SS
  doors_close: string | null;
};

export type DoorsWindow = {
  doors_open: string | null;
  doors_close: string | null;
};

export function doorsForDay(input: {
  perDay: DayDoors[];
  fallback: DoorsWindow;
  eventDate: string;
}): DoorsWindow {
  const { perDay, fallback, eventDate } = input;

  const override = perDay.find((d) => d.event_date === eventDate);
  if (override) {
    return {
      doors_open: override.doors_open ?? fallback.doors_open,
      doors_close: override.doors_close ?? fallback.doors_close,
    };
  }

  return {
    doors_open: fallback.doors_open,
    doors_close: fallback.doors_close,
  };
}

const toHHMM = (t: string | null | undefined): string =>
  t && t.length >= 5 ? t.substring(0, 5) : "";

// Build a per-day doors list: one entry per day in [startDate,
// endDate]. Times are HH:MM strings when resolvable, empty strings
// when neither per-day nor briefing-level values exist for that
// side. Callers decide how to render an empty side (form: "12:00"
// UI default; display: "Not set"; Analytics: skip the calc).
export function reconstructFestivalDays(input: {
  startDate: string;
  endDate: string;
  perDayDoors: DayDoors[];
  fallback: DoorsWindow;
}): Array<{ date: string; startTime: string; endTime: string }> {
  const { startDate, endDate, perDayDoors, fallback } = input;
  if (!startDate || !endDate) return [];

  // One day-expansion in the package, so a day selector and the page
  // prefetching that day can never disagree about which days exist.
  return eventDatesBetween(startDate, endDate).map((date) => {
    const window = doorsForDay({ perDay: perDayDoors, fallback, eventDate: date });
    return {
      date,
      startTime: toHHMM(window.doors_open),
      endTime: toHHMM(window.doors_close),
    };
  });
}

/** The briefing columns briefingDoorsDays reads. */
export type BriefingDoorsSource = {
  festival_date_start?: string | null;
  festival_date_end?: string | null;
  doors_open?: string | null;
  doors_close?: string | null;
};

// reconstructFestivalDays for a briefing row: its date range and its own
// doors as the fallback, with the per-day overrides on top. [] without both
// dates. The briefing screens of both native apps read their doors this way.
export function briefingDoorsDays(
  briefing: BriefingDoorsSource,
  perDayDoors: DayDoors[],
): Array<{ date: string; startTime: string; endTime: string }> {
  if (!briefing.festival_date_start || !briefing.festival_date_end) return [];
  return reconstructFestivalDays({
    startDate: briefing.festival_date_start,
    endDate: briefing.festival_date_end,
    perDayDoors,
    fallback: {
      doors_open: briefing.doors_open ?? null,
      doors_close: briefing.doors_close ?? null,
    },
  });
}

// Resolve the start / end Date pair for a single analytics day.
// Returns null when either side is unset — the caller (Analytics
// uptime calc) bails out rather than running against a fabricated
// window, which would produce misleading reliability numbers.
//
// Includes the overnight roll when doorsClose <= doorsOpen, so a
// 14:00 → 03:00 window correctly spans into the next morning for
// GPS-uptime accounting.
export function resolveAnalyticsWindow(input: {
  selectedDay: string;
  doorsOpen: string;
  doorsClose: string;
}): { start: Date; end: Date } | null {
  const open = toHHMM(input.doorsOpen);
  const close = toHHMM(input.doorsClose);
  if (!open || !close) return null;
  const start = new Date(`${input.selectedDay}T${open}:00`);
  let end = new Date(`${input.selectedDay}T${close}:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  if (end.getTime() <= start.getTime()) {
    end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
  }
  return { start, end };
}

// Build a doors snippet for non-UI consumers (AI prompt context,
// PDFs that prefer text). Returns:
//   - "" when the briefing has no date range, OR no day in the
//     range has any doors info on either side (don't put words in
//     the producer's mouth).
//   - "Doors: HH:MM - HH:MM" for a single-day briefing where both
//     sides are resolved.
//   - "Doors per day:" header + one indented "yyyy-mm-dd: open -
//     close" row per day for multi-day. A day with one or both
//     sides unset renders that side as "Not set" so the AI sees
//     the literal absence rather than a hallucinated default.
export function formatDoorsForContext(input: {
  startDate: string | null;
  endDate: string | null;
  perDayDoors: DayDoors[];
  fallback: DoorsWindow;
}): string {
  if (!input.startDate || !input.endDate) return "";
  const days = reconstructFestivalDays({
    startDate: input.startDate,
    endDate: input.endDate,
    perDayDoors: input.perDayDoors,
    fallback: input.fallback,
  });
  if (days.length === 0) return "";

  const anyConfigured = days.some((d) => d.startTime || d.endTime);
  if (!anyConfigured) return "";

  const fmt = (t: string) => t || "Not set";
  if (days.length === 1) {
    return `Doors: ${fmt(days[0].startTime)} - ${fmt(days[0].endTime)}`;
  }
  return [
    "Doors per day:",
    ...days.map((d) => `  ${d.date}: ${fmt(d.startTime)} - ${fmt(d.endTime)}`),
  ].join("\n");
}

/**
 * "12:00 - 23:00", or "Not set" for either side that is missing.
 *
 * Every screen that shows doors wrote this out by hand — four pages × two
 * ternaries — each with its own idea of the fallback text. The AI-context
 * builder above already owned the "Not set" convention; this is the same rule
 * for the screens, with the display format injected because that depends on
 * whose 12h/24h setting applies.
 */
export function formatDoorsRange(
  day: { startTime: string; endTime: string },
  formatTime: (time: string | null | undefined) => string,
): string {
  const side = (t: string) => (t ? formatTime(t) : "Not set");
  return `${side(day.startTime)} - ${side(day.endTime)}`;
}
